import http from './api'
import config from './config'

export interface AgentPredictionProposal {
  type: 'prediction'
  matchId: string
  winner: string | null
  predictedScore: string
  predictedWinnerAdditionalTime?: string | null
  predictedWinnerPenalties?: string | null
}

export interface AgentCopilotResponse {
  message: string
  proposal?: AgentPredictionProposal
  sources: string[]
  fromTemplate: boolean
  sessionId?: string
}

export interface AgentEnrollmentProposal {
  gameType: 'football' | 'formula1'
  tournamentIds: string[]
  tournamentNames: string[]
  countryCode: string
  city: string
}

export interface AgentOnboardingResponse extends AgentCopilotResponse {
  proposeEnrollment?: AgentEnrollmentProposal
  suggestCreateRoom?: boolean
}

export interface AgentInviteProposal {
  subject: string
  body: string
}

export interface AgentInviteDraftResponse {
  message: string
  proposeInvite: AgentInviteProposal
  sources: string[]
  fromTemplate: boolean
  sessionId?: string
}

export interface AgentF1GridProposal {
  type: 'f1_grid'
  raceId: string
  predictedPositions: Record<string, number>
}

export interface AgentF1CopilotResponse {
  message: string
  proposal?: AgentF1GridProposal
  sources: string[]
  fromTemplate: boolean
  sessionId?: string
}

export interface AgentAnalyticsDashboard {
  enabled: boolean
  dailyCapPerUser: number
  totalSessions: number
  last7Days: number
  last30Days: number
  templateRate: number
  avgLatencyMs: number
  byAgentType: Array<{ agentType: string; count: number; templateCount: number }>
}

const agentService = {
  async getStatus(): Promise<{
    enabled: boolean
    streaming?: boolean
    redis?: boolean
    redisRateLimits?: boolean
    observability?: { helicone: boolean; langfuse: boolean }
  }> {
    const res = await http.get('/agent/status')
    return res.data
  },

  async predictionCopilot(params: {
    roomId: string
    matchId: string
    sessionId?: string
    question?: string
    locale?: string
  }): Promise<AgentCopilotResponse> {
    const res = await http.post('/agent/copilot/prediction', params)
    return res.data
  },

  async predictionCopilotStream(
    params: {
      roomId: string
      matchId: string
      sessionId?: string
      question?: string
      locale?: string
    },
    callbacks: {
      onMeta?: (data: {
        sessionId: string
        proposal?: AgentPredictionProposal
        sources: string[]
        fromTemplate: boolean
      }) => void
      onToken?: (text: string) => void
      onDone?: (data: AgentCopilotResponse) => void
      onError?: (message: string) => void
    },
  ): Promise<AgentCopilotResponse | null> {
    const token = localStorage.getItem('token')
    const res = await fetch(`${config.baseURL}/api/agent/copilot/prediction/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(params),
    })

    if (!res.ok) {
      let message = 'Copilot unavailable'
      try {
        const err = await res.json()
        message = err.message ?? message
      } catch {
        message = res.statusText || message
      }
      callbacks.onError?.(message)
      throw new Error(message)
    }

    if (!res.body) {
      callbacks.onError?.('No stream body')
      return null
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let finalResponse: AgentCopilotResponse | null = null

    const dispatchEvent = (event: string, data: unknown) => {
      if (event === 'meta') {
        callbacks.onMeta?.(data as {
          sessionId: string
          proposal?: AgentPredictionProposal
          sources: string[]
          fromTemplate: boolean
        })
      } else if (event === 'token') {
        callbacks.onToken?.((data as { text: string }).text)
      } else if (event === 'done') {
        finalResponse = data as AgentCopilotResponse
        callbacks.onDone?.(finalResponse)
      } else if (event === 'error') {
        callbacks.onError?.((data as { message: string }).message)
      }
    }

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const chunks = buffer.split('\n\n')
      buffer = chunks.pop() ?? ''
      for (const chunk of chunks) {
        const lines = chunk.split('\n')
        let event = 'message'
        let dataLine = ''
        for (const line of lines) {
          if (line.startsWith('event:')) event = line.slice(6).trim()
          if (line.startsWith('data:')) dataLine += line.slice(5).trim()
        }
        if (!dataLine) continue
        try {
          dispatchEvent(event, JSON.parse(dataLine))
        } catch {
          // ignore malformed chunk
        }
      }
    }

    return finalResponse
  },

  async explainScoring(params: {
    roomId: string
    predictedScore?: string
    winner?: string | null
    question?: string
    locale?: string
  }): Promise<AgentCopilotResponse> {
    const res = await http.post('/agent/copilot/explain-scoring', params)
    return res.data
  },

  async onboardingConcierge(params: {
    gameType?: 'football' | 'formula1'
    message?: string
    countryCode?: string
    city?: string
    locale?: string
  }): Promise<AgentOnboardingResponse> {
    const res = await http.post('/agent/copilot/onboarding', params)
    return res.data
  },

  async inviteDraft(params: {
    roomId: string
    recipientEmail?: string
    message?: string
    locale?: string
  }): Promise<AgentInviteDraftResponse> {
    const res = await http.post('/agent/copilot/invite-draft', params)
    return res.data
  },

  async chessExplain(params: {
    roomId?: string
    fen: string
    moves: string[]
    moveIndex: number
    question?: string
    locale?: string
  }): Promise<AgentCopilotResponse> {
    const res = await http.post('/agent/copilot/chess-explain', params)
    return res.data
  },

  async f1GridCopilot(params: {
    roomId: string
    raceId: string
    question?: string
    locale?: string
  }): Promise<AgentF1CopilotResponse> {
    const res = await http.post('/agent/copilot/f1-grid', params)
    return res.data
  },

  async getAdminAnalytics(): Promise<AgentAnalyticsDashboard> {
    const res = await http.get('/agent/admin/analytics')
    return res.data
  },
}

export default agentService
